import { createQueryFn } from '@oneview/redux/utils';
import { showToast } from '@/redux';
import { api } from './baseApi';
import { DataTags } from './dataTags';

export interface PhotoDto {
    id: number;
    url: string;
    thumbnailUrl: string;
    description: string;
}

export const photoApi = api.injectEndpoints({
    endpoints: (build) => ({
        getPhotos: build.query({
            queryFn: createQueryFn(
                async (
                    params: {
                        watchmenUserId: string;
                    },
                    { extra: api },
                ) => {
                    const { data } = await api.middlewareSdk.get<PhotoDto[]>('crm/v1/photos', undefined, {
                        params,
                    });

                    return data;
                },
            ),
            providesTags: [DataTags.GetPhotos],
        }),
        getPhoto: build.query({
            queryFn: createQueryFn(
                async (
                    {
                        photoId,
                        watchmenUserId,
                    }: {
                        photoId: number;
                        watchmenUserId: string;
                    },
                    { extra: api },
                ) => {
                    const { data } = await api.middlewareSdk.get<PhotoDto>(`crm/v1/photos/${photoId}`, undefined, {
                        params: { watchmenUserId },
                    });

                    return data;
                },
            ),
            providesTags: [DataTags.GetPhotos],
        }),
        uploadPhoto: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        file,
                        description,
                        watchmenUserId,
                    }: {
                        file: File;
                        description: string;
                        watchmenUserId: string;
                    },
                    { extra: api, dispatch },
                ) => {
                    try {
                        const formData = new FormData();
                        formData.append('file', file);
                        formData.append('description', description);

                        const response = await api.middlewareSdk.post<PhotoDto>('crm/v1/photos', formData, {
                            params: { watchmenUserId },
                            headers: { 'Content-Type': 'multipart/form-data' },
                        });

                        dispatch(showToast('Photo uploaded successfully.'));

                        return response;
                    } catch {
                        dispatch(
                            showToast({
                                content: 'Failed to upload photo',
                            }),
                        );
                    }
                },
            ),
            invalidatesTags: [DataTags.GetPhotos],
        }),
        deletePhoto: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        photoId,
                        watchmenUserId,
                    }: {
                        photoId: number;
                        watchmenUserId: string;
                    },
                    { extra: api, dispatch },
                ) => {
                    try {
                        const response = await api.middlewareSdk.delete<void>(`crm/v1/photos/${photoId}`, undefined, {
                            params: { watchmenUserId },
                        });

                        dispatch(showToast('Photo deleted successfully.'));

                        return response;
                    } catch {
                        dispatch(
                            showToast({
                                content: "Sorry, we couldn't delete the photo. Please try again later.",
                            }),
                        );
                    }
                },
            ),
            invalidatesTags: [DataTags.GetPhotos],
        }),
    }),
});

export const { useGetPhotosQuery, useGetPhotoQuery, useUploadPhotoMutation, useDeletePhotoMutation } = photoApi;
